import { useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  isBlockedTrialDay,
  toISODate,
  getTrialDayDisabledMatchers,
} from "@/lib/trialDates";

/**
 * TrialDatePicker
 * - Popover-triggered day picker locked to today..today+120 days.
 * - Sundays, AU national + state-specific public holidays are visually disabled.
 * - Emits YYYY-MM-DD strings so the parent form value shape stays unchanged.
 */
export default function TrialDatePicker({
  value,
  onChange,
  state,
  testId = "trial-date-picker",
  placeholder = "Pick a date",
  className,
}) {
  const [open, setOpen] = useState(false);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const max = new Date(today);
  max.setDate(max.getDate() + 120);

  const selected = (() => {
    if (!value) return undefined;
    const [y, m, d] = value.split("-").map(Number);
    if (!y || !m || !d) return undefined;
    return new Date(y, m - 1, d);
  })();

  const disabledMatchers = [
    { before: today },
    { after: max },
    ...getTrialDayDisabledMatchers(state),
  ];

  const handleSelect = (day) => {
    if (!day) return;
    if (isBlockedTrialDay(day, state)) return;
    if (day < today || day > max) return;
    onChange(toISODate(day));
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-sm border border-input bg-background px-3 py-2 text-sm text-left ring-offset-background",
            "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
            !value && "text-muted-foreground",
            className,
          )}
        >
          <span>{selected ? format(selected, "EEE, d MMM yyyy") : placeholder}</span>
          <CalendarIcon className="h-4 w-4 opacity-60 shrink-0 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0" data-testid={`${testId}-popover`}>
        <Calendar
          mode="single"
          selected={selected}
          onSelect={handleSelect}
          disabled={disabledMatchers}
          defaultMonth={selected || today}
          weekStartsOn={1}
          initialFocus
        />
        <p className="px-3 pb-3 text-[11px] text-muted-foreground leading-relaxed max-w-[280px]">
          Sundays and Australian public holidays are unavailable{state ? ` — including ${state} state holidays` : ""}. The team&apos;s off.
        </p>
      </PopoverContent>
    </Popover>
  );
}
